/**
 * Invoicing List-Load Policy
 *
 * Ported from `erpsSyncPolicy`. Subscribes to `ExternalSystemsIntegrations` and
 * keeps only the provisioned integrations whose connector declares the
 * `issues-invoices` capability. The integrations readmodel stamps each row's
 * `capabilities` (see `integrations.readmodel.ts`), so we filter on the row
 * directly rather than making a second hop into the connectors catalog.
 *
 * The filtered, keyed record is dispatched into `data.integrations` via
 * `list/set` (which also clears `loading.listLoading`).
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType, ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
import { InvoicingState, InvoicingIntegration } from '../state';
import { InvoicingCommand } from '../commands';

/**
 * An integration is an invoicing integration iff its readmodel row declares the
 * `issues-invoices` capability.
 */
const isInvoicingIntegration = (
  integration: SystemIntegrationsReadmodel | undefined
): boolean =>
  Boolean( integration?.capabilities?.includes( 'issues-invoices' ));

/**
 * Creates a policy that loads the provisioned invoicing integrations from the
 * backend subscription into `data.integrations`.
 */
export const createInvoicingListLoadPolicy = (
  backend: ClientLibraryType
): Policy<InvoicingState, InvoicingCommand> => ({
  name: 'invoicing-list-load',

  init: ({ dispatch, getState }) => {
    const subscription = backend.subscriptions.ExternalSystemsIntegrations(
      {},
      ( change: ReadmodelChange<SystemIntegrationsReadmodel> ) => {
        if ( change.type === 'snapshot' ) {
          const invoicingRecord = change.data.items
            .filter( isInvoicingIntegration )
            .reduce<Record<string, InvoicingIntegration>>(
              ( acc, integration ) => {
                acc[integration.id] = integration;
                return acc;
              },
              {}
            );
          dispatch(
            { type: 'integrations/invoicing/list/set', payload: { integrations: invoicingRecord }},
            { source: 'api' }
          );
          return;
        }

        if ( change.type === 'signal' ) return;

        const record = change.type === 'delete' ? change.previous : change.data;
        if ( !isInvoicingIntegration( record )) return;

        const current = getState().data.integrations;

        if ( change.type === 'delete' ) {
          const { [change.previous.id]: _removed, ...rest } = current;
          dispatch(
            { type: 'integrations/invoicing/list/set', payload: { integrations: rest }},
            { source: 'api' }
          );
        } else {
          dispatch(
            {
              type: 'integrations/invoicing/list/set',
              payload: { integrations: { ...current, [change.data.id]: change.data }}
            },
            { source: 'api' }
          );
        }
      }
    );

    return () => {
      subscription?.close();
    };
  }
});
